import './HvacFormGroupView.scss';
import { HvacGroup } from '@belimo-retrofit-portal/logic';
import { FieldPath, FormController } from '@form-ts/core';
import { useFormField } from '@form-ts/react';
import clsx from 'clsx';
import { pipe } from 'fp-ts/function';
import { filterWithIndex } from 'fp-ts/ReadonlyArray';
import React, { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu';
import { Tab, TabProps } from 'src/modules/common/components/Tab';
import { TabList } from 'src/modules/common/components/TabList';
import { Tabs } from 'src/modules/common/components/Tabs';
import { FormError } from 'src/modules/form/types/FormError';
import { modifyField } from 'src/modules/form/utils/field';
import { HVAC_GROUP_AREAS } from 'src/modules/hvac/constants/group';
import { HvacConfig, HvacConfigSection } from 'src/modules/hvac/types/HvacConfig';
import { HvacFormData, HvacFormSectionData } from 'src/modules/hvac/types/HvacFormData';
import { HvacSchemaGroup } from 'src/modules/hvac/types/HvacSchema';
import { HvacSchemaAspectRatio } from 'src/modules/hvac/types/HvacSchemaAspectRatio';
import { HvacVersion } from 'src/modules/hvac/types/HvacVersion';
import { getDefaultFormGroup } from 'src/modules/hvac/utils/getDefaultFormGroup';
import { HvacFormAspectRatioBody } from 'src/modules/hvac/views/HvacFormAspectRatioBody';
import { HvacFormAspectRatioLabel } from 'src/modules/hvac/views/HvacFormAspectRatioLabel';
import { HvacFormGroupArea } from 'src/modules/hvac/views/HvacFormGroupArea';
import { HvacFormGroupDeleteConfirmationModal } from 'src/modules/hvac/views/HvacFormGroupDeleteConfirmationModal';
import { HvacFormGroupSchema } from 'src/modules/hvac/views/HvacFormGroupSchema';
import { HvacFormGroupTabControl } from 'src/modules/hvac/views/HvacFormGroupTabControl';

type Props = {
  readonly group: HvacGroup;
  readonly form: FormController<HvacFormData, FormError>;
  readonly config: HvacConfig;
  readonly schema: ReadonlyArray<HvacSchemaGroup>;
  readonly isAddingAllowed: boolean;
  readonly readOnly: boolean;
};

export const HvacFormGroupView = memo(({
  group,
  config,
  form,
  schema,
  isAddingAllowed,
  readOnly,
}: Props) => {
  const [schemaVersion, setSchemaVersion] = useState<HvacVersion>(HvacVersion.ACTUAL);
  const handleActualVersionSelect = useCallback(() => setSchemaVersion(HvacVersion.ACTUAL), []);
  const handleFutureVersionSelect = useCallback(() => setSchemaVersion(HvacVersion.FUTURE), []);

  const [activeTab, setActiveTab] = useState(0);
  const [openedSection, setOpenedSection] = useState<
  FieldPath<HvacFormData, FormError, HvacFormSectionData> | null
  >(null);
  const [aspectRatioOpened, setAspectRatioOpened] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<HvacSchemaAspectRatio>(HvacSchemaAspectRatio.HALF);

  const [focusedSection, setFocusedSection] = useState<HvacConfigSection | null>(null);

  useEffect(() => {
    if (openedSection !== null) {
      const value = openedSection.value.get(openedSection.form.currentState);

      setFocusedSection(value.config);
    } else {
      setFocusedSection(null);
    }
  }, [openedSection]);

  const handleTabCreate = useCallback(() => {
    const groupPath = form.field.at('assessment').at('groups').at(group);
    const groupValue = groupPath.value
      .get(form.currentState)
      .concat(getDefaultFormGroup(config[group]));

    form.change(pipe(
      form.currentState,
      groupPath.value.set(groupValue),
    ));
    setActiveTab(groupValue.length - 1);
  }, [form, group, config]);

  const groupListField = form.field.at('assessment').at('groups').at(group);
  const { value: groups } = useFormField(groupListField);

  const groupItemField = groupListField.at(Math.min(activeTab, groups.length));
  const { value: areas } = useFormField(form.field.at('assessment').at('areas'));

  const [deleteConfirmationIndex, setDeleteConfirmationIndex] = useState(0);
  const handleDeleteConfirmationOpen = useCallback((groupIndex: number) => {
    setDeleteConfirmationIndex(groupIndex);
  }, []);
  const handleDeleteConfirmationCancel = useCallback(() => {
    setDeleteConfirmationIndex(0);
  }, []);
  const handleDeleteConfirmationSubmit = useCallback(() => {
    if (activeTab >= deleteConfirmationIndex) {
      setActiveTab(activeTab - 1);
    }

    form.change(pipe(
      form.currentState,
      modifyField(
        form.field.at('assessment').at('groups').at(group),
        filterWithIndex((index) => index !== deleteConfirmationIndex),
      ),
    ));

    setDeleteConfirmationIndex(0);
  }, [activeTab, deleteConfirmationIndex, form, group]);

  return (
    <div className="bp-hvac-form-group-view">
      <div className="bp-hvac-form-group-view__tabs">
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
          <TabList>
            {groups.map((_, groupIndex): ReactElement<TabProps> => (
              // eslint-disable-next-line react/no-array-index-key
              <Tab theme="white" size="lg" key={groupIndex}>
                <HvacFormGroupTabControl
                  group={group}
                  index={groupIndex}
                  field={groupListField.at(groupIndex)}
                  readOnly={readOnly}
                  onDelete={!readOnly && groupIndex > 0 ? handleDeleteConfirmationOpen : undefined}
                />
              </Tab>
            ))}

            {isAddingAllowed && !readOnly ? (
              <Tab
                key="new"
                theme="white"
                size="lg"
                disabled={groups.length >= 3}
                tooltipValue={groups.length >= 3 ? <FormattedMessage id="hvac/form/common/tabs/limit"/> : null}
                onClick={handleTabCreate}
              >
                +
              </Tab>
            ) : null}
          </TabList>
        </Tabs>
      </div>
      <div className="bp-hvac-form-group-view__content">
        <div className={clsx({
          'bp-hvac-form-group-view__form': true,
          'bp-hvac-form-group-view__form--hidden': aspectRatio === HvacSchemaAspectRatio.FULL,
        })}
        >
          {HVAC_GROUP_AREAS[group].map((area) => (
            <div key={area} className="bp-hvac-form-group-view__area">
              <HvacFormGroupArea
                group={group}
                area={area}
                config={config}
                field={groupItemField.at('areas').at(area)}
                openedSection={openedSection}
                readOnly={readOnly}
                setOpenedSection={setOpenedSection}
              />
            </div>
          ))}
        </div>

        <div className={clsx({
          'bp-hvac-form-group-view__preview': true,
          [`bp-hvac-form-group-view__preview--${aspectRatio}`]: true,
        })}
        >
          <div className="bp-hvac-form-group-view__preview-version">
            <Button
              type="button"
              size="sm"
              kind={schemaVersion === HvacVersion.ACTUAL ? 'secondary' : 'tertiary'}
              onClick={handleActualVersionSelect}
            >
              <FormattedMessage id="hvac/form/common/actual"/>
            </Button>

            <Button
              className="bp-hvac-form-group-view__preview-version-button"
              type="button"
              size="sm"
              kind={schemaVersion === HvacVersion.FUTURE ? 'secondary' : 'tertiary'}
              onClick={handleFutureVersionSelect}
            >
              <FormattedMessage id="hvac/form/common/future"/>
            </Button>

            <OverflowMenu
              id="bp-hvac-form-group-aspect-ratio"
              opened={aspectRatioOpened}
              label={<HvacFormAspectRatioLabel onOpen={() => setAspectRatioOpened((prev) => !prev)} open={true}/>}
              setOpened={setAspectRatioOpened}
              buttonTooltip={null}
            >
              <HvacFormAspectRatioBody setAspectRatio={setAspectRatio}/>
            </OverflowMenu>
          </div>

          <div className="bp-hvac-form-group-view__preview-schema">
            <div className="bp-hvac-form-group-view__preview-schema-layers">
              <HvacFormGroupSchema
                key={schemaVersion}
                schema={schema[activeTab][schemaVersion].layers}
                focused={focusedSection}
              />
            </div>
          </div>
        </div>
      </div>

      <HvacFormGroupDeleteConfirmationModal
        isOpen={deleteConfirmationIndex > 0}
        onClose={handleDeleteConfirmationCancel}
        onSubmit={handleDeleteConfirmationSubmit}
      />
    </div>
  );
});
